import React, { useState } from "react";
import { NavLink, Routes, Route } from "react-router-dom";
import {
  Users, User, Package,
  Layers, FileText,
  Menu, LogOut, ChevronDown, LayoutDashboard,
  BookOpen, MapPin, Handshake, Grid3x3,
  TrendingUp, RotateCcw, ShoppingCart, ShoppingBag,
  ArrowLeftRight, ArrowDownToLine, ArrowUpFromLine,
  Wallet, Receipt, Database, Settings, Bell,
} from "lucide-react";
import "../styles/dashboard.css";

import Clients from "./Clients";
import UserScreen from "./UserScreen";
import Partners from "./Partners";
import Area from "./Area";
import ProductGroup from "./ProductGroup";

const menuGroups = [
  {
    group: "Dashboard",
    icon: LayoutDashboard,
    path: "overview",
    single: true,
  },
  {
    group: "Masters",
    icon: Database,
    children: [
      { path: "clients", label: "Clients", icon: Users },
      { path: "product", label: "Products", icon: Package },
      { path: "users", label: "Users", icon: User },
      { path: "partner", label: "Partners", icon: Handshake },
      { path: "account", label: "Chart of A/c", icon: BookOpen },
      {
        label: "Sub Masters",
        icon: Grid3x3,
        isSubGroup: true,
        children: [
          { path: "group", label: "Group Master", icon: Layers },
          { path: "area", label: "Area Master", icon: MapPin },
        ],
      },
    ],
  },
  {
    group: "Transactions",
    icon: FileText,
    children: [
      {
        label: "Inventory",
        icon: Package,
        isSubGroup: true,
        children: [
          { path: "sales", label: "Sales", icon: TrendingUp },
          { path: "sales-return", label: "Sales Return", icon: RotateCcw },
          { path: "purchase", label: "Purchase", icon: ShoppingCart },
          { path: "purchase-return", label: "Purchase Return", icon: ShoppingBag },
          {
            label: "Stock Transfer",
            icon: ArrowLeftRight,
            isSubGroup: true,
            children: [
              { path: "stock-inwards", label: "Inwards", icon: ArrowDownToLine },
              { path: "stock-outwards", label: "Outwards", icon: ArrowUpFromLine },
            ],
          },
        ],
      },
      {
        label: "Accounts",
        icon: Wallet,
        isSubGroup: true,
        children: [
          { path: "receipts", label: "Receipts", icon: Receipt },
          { path: "payments", label: "Payments", icon: Wallet },
        ],
      },
    ],
  },
  {
    group: "Admin",
    icon: Users,
    children: [
      { path: "users", label: "Users", icon: User },
    ],
  },
];

// ── Checks if any nested child path is active ────────
const isAnyChildActive = (children = []) =>
  children.some((c) =>
    c.children
      ? isAnyChildActive(c.children)
      : window.location.pathname.includes(`/${c.path}`)
  );

// ── Recursive SubGroup (nested menu) ────────────────
const SubGroup = ({ item, depth }) => {
  const anyActive = isAnyChildActive(item.children);
  const [open, setOpen] = useState(anyActive);

  return (
    <li className="sb-subgroup">
      <button
        className={`sb-subgroup-btn ${anyActive ? "active" : ""}`}
        style={{ paddingLeft: `${12 + depth * 8}px` }}
        onClick={() => setOpen((o) => !o)}
      >
        <item.icon size={13} className="sb-icon" />
        <span>{item.label}</span>
        <ChevronDown
          size={12}
          className={`sb-chevron ${open || anyActive ? "rotated" : ""}`}
        />
      </button>

      {(open || anyActive) && (
        <ul className="sb-submenu">
          {item.children.map((child, i) =>
            child.isSubGroup ? (
              <SubGroup key={i} item={child} depth={depth + 1} />
            ) : (
              <li key={child.path}>
                <NavLink
                  to={`/dashboard/${child.path}`}
                  className={({ isActive }) =>
                    `sb-sublink ${isActive ? "active" : ""}`
                  }
                  style={{ paddingLeft: `${20 + depth * 8}px` }}
                >
                  <child.icon size={13} className="sb-icon" />
                  <span>{child.label}</span>
                </NavLink>
              </li>
            )
          )}
        </ul>
      )}
    </li>
  );
};

// ── Top-level menu group ─────────────────────────────
const MenuItem = ({ item, collapsed }) => {
  const anyActive = !item.single && isAnyChildActive(item.children);
  const [open, setOpen] = useState(anyActive);

  if (item.single) {
    return (
      <li>
        <NavLink
          to={`/dashboard/${item.path}`}
          className={({ isActive }) => `sb-link ${isActive ? "active" : ""}`}
          title={collapsed ? item.group : ""}
        >
          <item.icon size={18} className="sb-icon" />
          {!collapsed && <span className="sb-label">{item.group}</span>}
        </NavLink>
      </li>
    );
  }

  return (
    <li className={`sb-group ${open || anyActive ? "open" : ""}`}>
      <button
        className={`sb-group-btn ${anyActive ? "active" : ""}`}
        onClick={() => !collapsed && setOpen((o) => !o)}
        title={collapsed ? item.group : ""}
      >
        <item.icon size={18} className="sb-icon" />
        {!collapsed && (
          <>
            <span className="sb-label">{item.group}</span>
            <ChevronDown
              size={14}
              className={`sb-chevron ${open || anyActive ? "rotated" : ""}`}
            />
          </>
        )}
      </button>

      {/* Expanded submenu */}
      {!collapsed && (open || anyActive) && (
        <ul className="sb-submenu">
          {item.children.map((child, i) =>
            child.isSubGroup ? (
              <SubGroup key={i} item={child} depth={1} />
            ) : (
              <li key={child.path}>
                <NavLink
                  to={`/dashboard/${child.path}`}
                  className={({ isActive }) =>
                    `sb-sublink ${isActive ? "active" : ""}`
                  }
                >
                  <child.icon size={14} className="sb-icon" />
                  <span>{child.label}</span>
                </NavLink>
              </li>
            )
          )}
        </ul>
      )}

      {/* Collapsed: hover tooltip */}
      {collapsed && (
        <div className="sb-tooltip">
          <div className="sb-tooltip-title">{item.group}</div>
          {item.children.map((child, i) =>
            child.isSubGroup ? (
              <div key={i}>
                <div className="sb-tooltip-section">{child.label}</div>
                {child.children?.map((c) =>
                  c.isSubGroup ? (
                    <div key={c.label}>
                      <div className="sb-tooltip-section" style={{ paddingLeft: 16 }}>{c.label}</div>
                      {c.children?.map((cc) => (
                        <NavLink key={cc.path} to={`/dashboard/${cc.path}`} className="sb-tooltip-link" style={{ paddingLeft: 24 }}>
                          {cc.label}
                        </NavLink>
                      ))}
                    </div>
                  ) : (
                    <NavLink key={c.path} to={`/dashboard/${c.path}`} className="sb-tooltip-link" style={{ paddingLeft: 16 }}>
                      {c.label}
                    </NavLink>
                  )
                )}
              </div>
            ) : (
              <NavLink key={child.path} to={`/dashboard/${child.path}`} className="sb-tooltip-link">
                {child.label}
              </NavLink>
            )
          )}
        </div>
      )}
    </li>
  );
};

// ── Profile Dropdown ─────────────────────────────────
const ProfileDropdown = () => {
  const [open, setOpen] = useState(false);
  const ref = React.useRef(null);

  React.useEffect(() => {
    const handleOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutside);
    document.addEventListener("touchstart", handleOutside);
    return () => {
      document.removeEventListener("mousedown", handleOutside);
      document.removeEventListener("touchstart", handleOutside);
    };
  }, []);

  return (
    <div className="profile-wrapper" ref={ref}>
      {/* Backdrop for mobile */}
      {open && (
        <div
          className="profile-backdrop"
          onClick={() => setOpen(false)}
        />
      )}

      <button
        className={`profile-trigger ${open ? "open" : ""}`}
        onClick={() => setOpen((o) => !o)}
        aria-label="Open profile menu"
        aria-expanded={open}
      >
        <div className="avatar">A</div>
        <span className="username">Admin</span>
        <ChevronDown
          size={14}
          className={`profile-chevron ${open ? "rotated" : ""}`}
        />
      </button>

      {open && (
        <div className="profile-dropdown">
          {/* User info header */}
          <div className="profile-dropdown-header">
            <div className="profile-dropdown-avatar">A</div>
            <div className="profile-dropdown-info">
              <span className="profile-dropdown-name">Admin User</span>
              <span className="profile-dropdown-email">admin@xqlusive.com</span>
            </div>
          </div>

          <div className="profile-dropdown-divider" />

          <NavLink
            to="/dashboard/users"
            className="profile-dropdown-item"
            onClick={() => setOpen(false)}
          >
            <User size={15} />
            <span>My Profile</span>
          </NavLink>

          <button className="profile-dropdown-item">
            <Settings size={15} />
            <span>Settings</span>
          </button>

          <button className="profile-dropdown-item">
            <Bell size={15} />
            <span>Notifications</span>
          </button>

          <div className="profile-dropdown-divider" />

          <button className="profile-dropdown-item profile-dropdown-logout">
            <LogOut size={15} />
            <span>Logout</span>
          </button>
        </div>
      )}
    </div>
  );
};

// ── Dashboard Layout ─────────────────────────────────
const Dashboard = () => {
  const [collapsed, setCollapsed] = useState(false);

  return (
    <div className="dashboard">
      <aside className={`sidebar ${collapsed ? "collapsed" : ""}`}>
        <div className="sidebar-header">
          {!collapsed && (
            <div className="sidebar-brand">
              <span className="brand-x">XQ</span>
              <span className="brand-rest">lusive</span>
            </div>
          )}
          <button className="collapse-btn" onClick={() => setCollapsed(!collapsed)}>
            <Menu size={19} />
          </button>
        </div>

        <nav className="sidebar-nav">
          <ul className="sidebar-menu">
            {menuGroups.map((item) => (
              <MenuItem key={item.group} item={item} collapsed={collapsed} />
            ))}
          </ul>
        </nav>

        <div className="sidebar-footer">
          {!collapsed && <span className="sb-version">XQlusive v1.0</span>}
        </div>
      </aside>

      <div className="main-area">
        <header className="topbar">
          <div className="topbar-left">
            <span className="page-title">Dashboard</span>
          </div>
          <ProfileDropdown />
        </header>

        <main className="dashboard-content">
          <Routes>
            <Route path="overview" element={<h2>Overview</h2>} />
            <Route path="clients" element={<Clients />} />
            <Route path="users" element={<UserScreen />} />
            <Route path="party" element={<h2>Party</h2>} />
            <Route path="product" element={<h2>Product</h2>} />
            <Route path="company" element={<h2>Company</h2>} />
            <Route path="van" element={<h2>Van</h2>} />
            <Route path="route" element={<h2>Route</h2>} />
            <Route path="account" element={<h2>Account Master</h2>} />
            <Route path="group" element={<ProductGroup />} />
            <Route path="area" element={<Area />} />
            <Route path="partner" element={<Partners />} />
            <Route path="sales" element={<h2>Sales</h2>} />
            <Route path="sales-return" element={<h2>Sales Return</h2>} />
            <Route path="purchase" element={<h2>Purchase</h2>} />
            <Route path="purchase-return" element={<h2>Purchase Return</h2>} />
            <Route path="stock-inwards" element={<h2>Stock Inwards</h2>} />
            <Route path="stock-outwards" element={<h2>Stock Outwards</h2>} />
            <Route path="receipts" element={<h2>Receipts</h2>} />
            <Route path="payments" element={<h2>Payments</h2>} />
          </Routes>
        </main>
      </div>
    </div>
  );
};

export default Dashboard;